import { useEffect, useState } from 'react';

import { api } from '../api/endpoints';
import type { AppSettings, ReferralRate } from '../api/types';

type Draft = Record<string, string | boolean>;

function toDraft(data: AppSettings): Draft {
  const draft: Draft = {};
  for (const param of data.params) {
    draft[param.key] = param.kind === 'bool' ? Boolean(param.value) : String(param.value ?? '');
  }
  for (const rate of data.rates) {
    draft[rateKey(rate)] = String(rate.rate_percent);
  }
  return draft;
}

function rateKey(rate: { category_code: string; level: number }): string {
  return `rate:${rate.category_code}:${rate.level}`;
}

/** Группировка по направлениям: ставка — это матрица «направление ×
 *  линия», и читать её списком из девяти строк невозможно. */
function byCategory(rates: ReferralRate[]): [string, ReferralRate[]][] {
  const map = new Map<string, ReferralRate[]>();
  for (const rate of rates) {
    const list = map.get(rate.category_code) ?? [];
    list.push(rate);
    map.set(rate.category_code, list);
  }
  return [...map.entries()];
}

export function SettingsPage() {
  const [data, setData] = useState<AppSettings | null>(null);
  const [draft, setDraft] = useState<Draft>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .settings()
      .then((loaded) => {
        setData(loaded);
        setDraft(toDraft(loaded));
      })
      .catch(() => setError('Раздел недоступен.'));
  }, []);

  function set(key: string, next: string | boolean) {
    setDraft((current) => ({ ...current, [key]: next }));
    setSaved(false);
  }

  async function save() {
    if (!data) return;
    setSaving(true);
    setError(null);
    try {
      // Отправляем только изменённое: правка ставки заводит новую
      // версию, и лишняя запись засорила бы историю процентов.
      const original = toDraft(data);

      const values: Record<string, string | number | boolean> = {};
      for (const param of data.params) {
        if (draft[param.key] === original[param.key]) continue;
        const raw = draft[param.key];
        values[param.key] =
          param.kind === 'bool'
            ? Boolean(raw)
            : param.kind === 'text'
              ? String(raw)
              : Number(raw) || 0;
      }

      const rates = data.rates
        .filter((rate) => draft[rateKey(rate)] !== original[rateKey(rate)])
        .map((rate) => ({
          category_code: rate.category_code,
          level: rate.level,
          rate_percent: Number(draft[rateKey(rate)]) || 0,
        }));

      let fresh = data;
      if (Object.keys(values).length > 0) fresh = await api.saveSettings(values);
      if (rates.length > 0) fresh = await api.saveRates(rates);

      setData(fresh);
      setDraft(toDraft(fresh));
      setSaved(true);
    } catch {
      setError('Не сохранилось. Проверьте значения и попробуйте ещё раз.');
    } finally {
      setSaving(false);
    }
  }

  if (!data) {
    return (
      <div className="screen">
        <h1 className="screen__title">Настройки</h1>
        {error ? <p className="muted">{error}</p> : <div className="skeleton" />}
      </div>
    );
  }

  const groups = [...new Set(data.params.map((param) => param.group))];

  // Пересчитывается на лету: видно, во сколько обойдётся правка,
  // до того как её сохранили.
  const liveTotal = (code: string): number =>
    data.rates
      .filter((rate) => rate.category_code === code)
      .reduce((sum, rate) => sum + (Number(draft[rateKey(rate)]) || 0), 0);

  return (
    <div className="screen">
      <h1 className="screen__title">Настройки</h1>
      <p className="muted">
        Меняют только администраторы. Токены, база и адреса живут в окружении
        сервера — их отсюда не тронуть, для этого нужна выкатка.
      </p>

      <section className="block">
        <h2 className="block__title">Реферальные проценты</h2>
        <p className="muted">
          Прошлые начисления не пересчитываются: новая ставка действует с
          сегодняшнего дня, старая остаётся в истории. Иначе вчерашнее
          начисление стало бы нечем объяснить.
        </p>

        {byCategory(data.rates).map(([code, rates]) => (
          <div key={code} className="ratecard">
            <div className="ratecard__head">
              <span className="ratecard__name">{rates[0].category_name}</span>
              <span
                className={`ratecard__total${liveTotal(code) > 60 ? ' ratecard__total--warn' : ''}`}
              >
                итого {liveTotal(code)}%
              </span>
            </div>
            {rates.map((rate) => (
              <label key={rate.level} className="rateline">
                <span className="rateline__label">
                  {rate.level_label}
                  <span className="muted small"> — {rate.level_hint}</span>
                </span>
                <span className="rateline__input">
                  <input
                    className="field__input"
                    inputMode="decimal"
                    value={String(draft[rateKey(rate)] ?? '')}
                    onChange={(event) => set(rateKey(rate), event.target.value)}
                  />
                  <span className="rateline__pct">%</span>
                </span>
              </label>
            ))}
          </div>
        ))}
        <p className="muted small">
          «Итого» — сколько уходит бонусами с каждой оплаты по всей цепочке.
          Это и есть цена реферальной программы.
        </p>
      </section>

      {groups.map((group) => (
        <section key={group} className="block">
          <h2 className="block__title">{group}</h2>
          {data.params
            .filter((param) => param.group === group)
            .map((param) =>
              param.kind === 'bool' ? (
                <label key={param.key} className="checkline">
                  <input
                    type="checkbox"
                    checked={Boolean(draft[param.key])}
                    onChange={(event) => set(param.key, event.target.checked)}
                  />
                  <span>
                    <b>{param.label}</b>
                    <br />
                    {param.hint}
                  </span>
                </label>
              ) : (
                <label key={param.key} className="field">
                  <span className="field__label">{param.label}</span>
                  <input
                    className="field__input"
                    inputMode={param.kind === 'text' ? 'text' : 'numeric'}
                    value={String(draft[param.key] ?? '')}
                    onChange={(event) => set(param.key, event.target.value)}
                  />
                  <span className="field__hint">{param.hint}</span>
                </label>
              ),
            )}
        </section>
      ))}

      <div className="savebar">
        {error && <span className="savebar__error">{error}</span>}
        <button className="btn" onClick={save} disabled={saving}>
          {saving ? 'Сохраняю…' : saved ? 'Сохранено' : 'Сохранить'}
        </button>
      </div>
    </div>
  );
}
